import {
    IconArrowBackUp,
    IconArrowForwardUp,
    IconBold,
    IconCalculator,
    IconCircleFilled,
    IconItalic,
    IconList,
    IconListNumbers,
    IconMarkdown,
    IconPhoto,
    IconShare,
    IconStrikethrough,
    IconTable,
    IconTypography
} from '@tabler/icons-react';
import { toggleMark as pmToggleMark } from '@milkdown/kit/prose/commands';
import { undo as historyUndo, redo as historyRedo } from '@milkdown/kit/prose/history';
import { undoCommand as yUndo, redoCommand as yRedo } from 'y-prosemirror';
import { wrapInList } from '@milkdown/kit/prose/schema-list';

import { useCallback, useMemo, useRef } from 'react';

import { ActionIcon, Center, Divider, Group, SegmentedControl, Text, Tooltip } from '@mantine/core';

import { useGetApiProductiveServiceNames } from '../../../api/productive/productive';
import { IdentityType } from '../../auth/IdentityType';
import { useEditorAdapter } from '../core/EditorAdapterContext';
import { encodeBlockData } from '../core/data-encoding';
import { createDefaultCostEstimateData } from '../directives/utils/costEstimateUtils';
import type { InsertAction } from '../plugins/InsertModeDropdown';
import ToolbarChatButton from '../plugins/ToolbarChatButton';
import BlockTypeDropdown from '../toolbar/BlockTypeDropdown';
import InsertBreakButton from '../toolbar/InsertBreakButton';
import InsertComponentDropdown from '../toolbar/InsertComponentDropdown';
import ToolbarExportDocxButton from '../toolbar/ToolbarExportDocxButton';

import { fileToDataUrl } from './image-upload-plugin';
import type { MilkdownAdapter } from './milkdown-adapter';
import type { CollabStatus } from './collab-plugin';
import type { AutoSaveStatus } from './MilkdownEditor';

interface MilkdownToolbarProps {
    adapter: MilkdownAdapter;
    exportDocx: () => void;
    openBpmnGenerator: () => void;
    userIdentity: IdentityType;
    documentContent: string;
    getCurrentMarkdown: () => string;
    collabStatus: CollabStatus;
    peerCount: number;
    autoSaveStatus: AutoSaveStatus;
    sourceView?: boolean;
    onToggleSourceView?: () => void;
}

function ToolbarButton({
    icon,
    label,
    onClick,
    disabled
}: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    disabled?: boolean;
}) {
    return (
        <Tooltip label={label} position="bottom">
            <ActionIcon
                variant="subtle"
                size="lg"
                color="gray"
                style={{
                    color: 'var(--mantine-color-text)',
                    opacity: disabled ? 0.25 : 1,
                    pointerEvents: disabled ? 'none' : undefined
                }}
                onClick={onClick}
            >
                {icon}
            </ActionIcon>
        </Tooltip>
    );
}

function getStatusIndicator(
    collabStatus: CollabStatus,
    autoSaveStatus: AutoSaveStatus
): { color: string; label: string; tooltip: string } {
    // Collab errors take priority
    if (collabStatus === 'auth-failed') {
        return { color: 'red', label: 'Auth failed', tooltip: 'Authentication failed — changes will not be saved. Export a copy, then refresh.' };
    }
    if (collabStatus === 'disconnected') {
        return { color: 'red', label: 'Disconnected', tooltip: 'Changes will not be saved — export a copy, then refresh if this persists' };
    }
    if (collabStatus === 'connecting') {
        return { color: 'yellow', label: 'Connecting...', tooltip: 'Connecting to collaboration server' };
    }

    // Collab is connected — show auto-save status
    if (autoSaveStatus === 'error') {
        return { color: 'red', label: 'Save failed', tooltip: 'Failed to save — retrying...' };
    }
    if (autoSaveStatus === 'saving') {
        return { color: 'yellow', label: 'Saving...', tooltip: 'Saving changes' };
    }
    if (autoSaveStatus === 'saved') {
        return { color: 'green', label: 'Saved', tooltip: 'All changes saved' };
    }

    // idle — connected, no pending changes
    return { color: 'green', label: 'Connected', tooltip: 'Connected to collaboration server' };
}

function StatusArea({
    collabStatus,
    peerCount,
    autoSaveStatus
}: {
    collabStatus: CollabStatus;
    peerCount: number;
    autoSaveStatus: AutoSaveStatus;
}) {
    const status = getStatusIndicator(collabStatus, autoSaveStatus);

    return (
        <Tooltip label={status.tooltip} position="bottom">
            <Group gap={4} style={{ cursor: 'default', marginLeft: 'auto' }}>
                <IconCircleFilled
                    size={10}
                    color={`var(--mantine-color-${status.color}-6)`}
                />
                <Text size="xs" c="dark.2">
                    {status.label}
                </Text>
                {peerCount > 0 && (
                    <Text size="xs" c="dark.2">
                        &middot; {peerCount + 1} editors
                    </Text>
                )}
            </Group>
        </Tooltip>
    );
}

function MilkdownToolbar({
    adapter,
    exportDocx,
    openBpmnGenerator,
    userIdentity,
    documentContent,
    getCurrentMarkdown,
    collabStatus,
    peerCount,
    autoSaveStatus,
    sourceView,
    onToggleSourceView
}: MilkdownToolbarProps) {
    const editorAdapter = useEditorAdapter();
    const { data: serviceNames } = useGetApiProductiveServiceNames();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const toggleMark = useCallback(
        (markName: string) => {
            adapter.runProseMirrorCommand((view) => {
                const { state, dispatch } = view;
                const markType = state.schema.marks[markName];
                if (!markType) return;
                pmToggleMark(markType)(state, dispatch);
                view.focus();
            });
        },
        [adapter]
    );

    // Use y-prosemirror undo/redo when collab is active (yUndoPlugin is loaded),
    // fall back to prosemirror-history for non-collab (DevOps read-only won't
    // reach here, but kept for safety).
    const isCollabActive = collabStatus === 'connected' || collabStatus === 'connecting';

    const handleUndo = useCallback(() => {
        adapter.runProseMirrorCommand((view) => {
            const undoFn = isCollabActive ? yUndo : historyUndo;
            undoFn(view.state, view.dispatch);
            view.focus();
        });
    }, [adapter, isCollabActive]);

    const handleRedo = useCallback(() => {
        adapter.runProseMirrorCommand((view) => {
            const redoFn = isCollabActive ? yRedo : historyRedo;
            redoFn(view.state, view.dispatch);
            view.focus();
        });
    }, [adapter, isCollabActive]);

    const handleToggleList = useCallback(
        (listType: 'bullet_list' | 'ordered_list') => {
            adapter.runProseMirrorCommand((view) => {
                const { state, dispatch } = view;
                const nodeType = state.schema.nodes[listType];
                if (!nodeType) return;
                wrapInList(nodeType)(state, dispatch);
                view.focus();
            });
        },
        [adapter]
    );

    const handleInsertTable = useCallback(() => {
        editorAdapter.insertMarkdown(
            '\n\n| Header 1 | Header 2 | Header 3 |\n| --- | --- | --- |\n| Cell 1 | Cell 2 | Cell 3 |\n\n'
        );
    }, [editorAdapter]);

    const handleInsertImage = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleImageFileSelected = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const dataUrl = await fileToDataUrl(file);
            editorAdapter.insertMarkdown(`\n\n![${file.name}](${dataUrl})\n\n`);

            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        },
        [editorAdapter]
    );

    const extraActions: InsertAction[] = useMemo(() => {
        const insertCostEstimate = () => {
            if (!serviceNames) return;

            const productiveServices = serviceNames.map(
                (service: { name: string }) => service.name
            );
            const defaultData = createDefaultCostEstimateData(
                productiveServices,
                undefined
            );
            const base64Data = encodeBlockData(defaultData);

            const markdownContent = `\n\nThe estimate provided below is a best-efforts approximation of the costs of the project based on the known requirements. This estimate is valid for 30 days from the date of this proposal. Actual costs may vary due to changes in project scope, client-caused delays, unforeseen circumstances, or other factors outside our control. During project execution and delivery, the Project Manager will notify the project stakeholders if factors affecting the timeline or cost arise and will strive to deliver the project in the expected time, scope and to the agreed budget.\n\nPrices exclude VAT.\n\n::costEstimate{data="${base64Data}"}\n\n**Resources**\n\nDeveloper(s): Provides technical input during workflow analysis, architecture planning, and UI wireframing preparation. Supports the identification of potential implementation challenges and contribute to risk assessments from a developer's perspective.\nTechnical Lead: Leads the technical advisory effort, ensuring that proposed solutions align with best practices and relevant standards. Reviews and advises on software architecture, risk planning, and requirements to support scalable and compliant development.\nProject Manager: Manages the project timeline, coordinates stakeholder sessions, and ensures clear communication and alignment across all parties. Responsible for tracking progress across activities and delivering consolidated outputs on schedule.\n\n`;

            editorAdapter.insertMarkdown(markdownContent);
        };

        return [
            {
                id: 'cost-estimate',
                callback: insertCostEstimate,
                icon: <IconCalculator size={16} />,
                title: 'Cost Estimate',
                description: 'Add cost estimation table'
            },
            {
                id: 'bpmn-generator',
                callback: openBpmnGenerator,
                icon: <IconShare size={16} />,
                title: 'BPMN Diagram',
                description: 'Generate BPMN process diagram'
            }
        ];
    }, [editorAdapter, serviceNames, openBpmnGenerator]);

    return (
        <Group
            gap={4}
            p="xs"
            style={{
                borderTop: '1px solid var(--mantine-color-default-border)',
                borderBottom: '1px solid var(--mantine-color-default-border)',
                flexWrap: 'wrap',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'var(--mantine-color-body)'
            }}
        >
            <ToolbarButton
                icon={<IconBold size={18} />}
                label="Bold (Ctrl+B)"
                onClick={() => toggleMark('strong')}
                disabled={sourceView}
            />
            <ToolbarButton
                icon={<IconItalic size={18} />}
                label="Italic (Ctrl+I)"
                onClick={() => toggleMark('emphasis')}
                disabled={sourceView}
            />
            <ToolbarButton
                icon={<IconStrikethrough size={18} />}
                label="Strikethrough"
                onClick={() => toggleMark('strike_through')}
                disabled={sourceView}
            />

            <Divider orientation="vertical" />

            <BlockTypeDropdown disabled={sourceView} />

            <Divider orientation="vertical" />

            <ToolbarButton
                icon={<IconPhoto size={18} />}
                label="Insert Image"
                onClick={handleInsertImage}
                disabled={sourceView}
            />
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageFileSelected}
            />
            <ToolbarButton
                icon={<IconTable size={18} />}
                label="Insert Table"
                onClick={handleInsertTable}
                disabled={sourceView}
            />
            <ToolbarButton
                icon={<IconList size={18} />}
                label="Bullet List"
                onClick={() => handleToggleList('bullet_list')}
                disabled={sourceView}
            />
            <ToolbarButton
                icon={<IconListNumbers size={18} />}
                label="Ordered List"
                onClick={() => handleToggleList('ordered_list')}
                disabled={sourceView}
            />

            <Divider orientation="vertical" />

            <InsertBreakButton disabled={sourceView} />
            <InsertComponentDropdown extraActions={extraActions} disabled={sourceView} />

            <Divider orientation="vertical" />

            <ToolbarButton
                icon={<IconArrowBackUp size={18} />}
                label="Undo (Ctrl+Z)"
                onClick={handleUndo}
                disabled={sourceView}
            />
            <ToolbarButton
                icon={<IconArrowForwardUp size={18} />}
                label="Redo (Ctrl+Shift+Z)"
                onClick={handleRedo}
                disabled={sourceView}
            />

            <Divider orientation="vertical" />

            <ToolbarChatButton getCurrentMarkdown={getCurrentMarkdown} />
            {onToggleSourceView && (
                <Tooltip
                    label={
                        peerCount > 0 && !sourceView
                            ? 'Markdown source is unavailable while others are editing'
                            : sourceView
                              ? 'Switch to rich editor'
                              : 'Switch to markdown source'
                    }
                    position="bottom"
                >
                    <SegmentedControl
                        size="xs"
                        value={sourceView ? 'source' : 'rich'}
                        onChange={onToggleSourceView}
                        data={[
                            {
                                value: 'rich',
                                label: (
                                    <Center style={{ gap: 4 }}>
                                        <IconTypography size={16} />
                                    </Center>
                                )
                            },
                            {
                                // Raw-markdown editing whole-doc-replaces the
                                // document on toggle-back, which clobbers peers'
                                // concurrent edits. Only allow it when solo.
                                value: 'source',
                                disabled: peerCount > 0,
                                label: (
                                    <Center style={{ gap: 4 }}>
                                        <IconMarkdown size={16} />
                                    </Center>
                                )
                            }
                        ]}
                    />
                </Tooltip>
            )}
            <ToolbarExportDocxButton
                exportDocx={exportDocx}
                userIdentity={userIdentity}
                documentContent={documentContent}
            />
            <StatusArea
                collabStatus={collabStatus}
                peerCount={peerCount}
                autoSaveStatus={autoSaveStatus}
            />
        </Group>
    );
}

export default MilkdownToolbar;
